import React from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useContext, useMemo, useEffect, useState } from 'react';
import { AppContext } from '../../core/Contexts/AppContext';
import { getMenuItems } from '../../core/services/menu';

// Map static routes to human-readable labels
const STATIC_LABELS = {
  '/': 'მთავარი',
  '/news': 'სიახლეები',
  '/contact': 'კონტაქტი',
  '/statistics': 'სტატისტიკა',
  '/faq': 'ხშირად დასმულიკითხვები',
  '/legislation': 'კანონმდებლობა',
  '/dangerproducts': 'საფრთხის შემცველი პროდუქტები',
  '/aboutdangerproducts': 'განაცხადე საფრთხის შემცველ პროდუქტზე',
  '/publicinfo': 'საჯარო ინფორმაცია',
  '/penalty': 'სამართალდარღვევის ოქმები და დადგენილებები',
};

const routeMatchers = [
  { pattern: '/news/detail/:id', label: 'სიახლე' },
  { pattern: '/dangerproducts/detail/:id', label: 'დეტალურად' },
  { pattern: '/page/:menu_id', labelFromTitle: true },
  { pattern: '/penalty/detail/:id', label: 'დეტალები' },
];

const DANGER_PRODUCTS_RETURN_KEY = 'danger-products-return-state';

const Breadcrumbs = () => {
  const location = useLocation();
  const { title } = useContext(AppContext);
  const [menuTree, setMenuTree] = useState([]);

  // Load menu structure to resolve parent/child relationships for /page/:menu_id
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getMenuItems();
        if (mounted) setMenuTree(data || []);
      } catch (e) {
        // ignore – breadcrumb will still render current label
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const items = useMemo(() => {
    const crumbs = [];
    // Home always first
    crumbs.push({ to: '/', label: STATIC_LABELS['/'] });

    // Try exact static label
    if (STATIC_LABELS[location.pathname]) {
      crumbs.push({ label: STATIC_LABELS[location.pathname] });
      return crumbs;
    }

    const dangerProductsReturnTo = (() => {
      try {
        const savedState = JSON.parse(
          sessionStorage.getItem(DANGER_PRODUCTS_RETURN_KEY) || 'null',
        );
        return savedState?.returnTo || '/dangerproducts';
      } catch (error) {
        return '/dangerproducts';
      }
    })();

    // Try dynamic route matchers
    for (const m of routeMatchers) {
      const match = matchPath(
        { path: m.pattern, end: true },
        location.pathname,
      );
      if (match) {
        // Build parent if exists (e.g., /news for /news/detail/:id)
        if (m.pattern.startsWith('/news/detail')) {
          crumbs.push({ to: '/news', label: STATIC_LABELS['/news'] });
        }
        if (m.pattern.startsWith('/dangerproducts/detail')) {
          crumbs.push({
            to: dangerProductsReturnTo,
            label: STATIC_LABELS['/dangerproducts'],
          });
        }
        if (m.pattern.startsWith('/penalty/detail')) {
          crumbs.push({ to: '/penalty', label: STATIC_LABELS['/penalty'] });
        }
        // If dynamic page route, include its parent submenu when available
        if (m.pattern.startsWith('/page/')) {
          const currentId = match.params?.menu_id;
          if (currentId && menuTree && menuTree.length) {
            // Look for the item and its parent
            for (const parent of menuTree) {
              if (String(parent.menu_id) === String(currentId)) {
                // current is a top-level item – no extra parent besides Home
                break;
              }
              const child = (parent.children || []).find(
                (c) => String(c.menu_id) === String(currentId),
              );
              if (child) {
                crumbs.push({
                  to: `/page/${parent.menu_id}`,
                  label: parent.title || parent.menu_name || '',
                });
                break;
              }
            }
          }
        }

        const label = m.labelFromTitle ? title || '' : m.label;
        crumbs.push({ label: label || '' });
        return crumbs;
      }
    }

    // Generic split for unknown routes: /a/b/c -> მთავარი > a > b > c
    const segments = location.pathname.split('/').filter(Boolean);
    let pathAcc = '';
    segments.forEach((seg, idx) => {
      pathAcc += `/${seg}`;
      const isLast = idx === segments.length - 1;
      const pretty = decodeURIComponent(seg.replaceAll('-', ' '));
      if (isLast) crumbs.push({ label: pretty });
      else crumbs.push({ to: pathAcc, label: pretty });
    });
    return crumbs;
  }, [location.pathname, title, menuTree]);

  // Hide on homepage and contact page
  if (location.pathname === '/' || location.pathname === '/contact')
    return null;

  return (
    <div className='breadcrumbs-wrapper py-2 '>
      <div className='container'>
        <nav aria-label='breadcrumb'>
          <ol className='breadcrumb mb-0'>
            {items.map((item, idx) => {
              const isLast = idx === items.length - 1;
              return (
                <li
                  key={idx}
                  className={`breadcrumb-item${isLast ? ' active' : ''}`}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.to && !isLast ? (
                    <Link to={item.to}>{item.label}</Link>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );
};

export default Breadcrumbs;
